import { z } from "zod";
import {
  INVESTOR_TYPE,
  SUB_TYPE,
  TYPE,
  TITLE,
  DATE,
  YEAR,
  DESCRIPTION,
  UPLOAD,
  RATING_TYPE,
  RATING_NAME,
  RATING_DESCRIPTION,
  ADDITIONAL_UPLOAD,
  THUMBNAIL_UPLOAD,
  UPLOAD_TRANSCRIPT,
  UPLOAD_LOGO,
  LINK,
} from "./consts";
import { getFieldConfig } from "./fieldConfig";

// Base schema with ALL possible fields
const baseSchema = {
  [INVESTOR_TYPE]: z.string().min(1, "Investor type is required"),
  [SUB_TYPE]: z.string().min(1, "Sub type is required"),
  [TYPE]: z.string().min(1, "Type is required"),
  [TITLE]: z.string().min(1, "Title is required"),
  [DATE]: z.date().nullable().optional(),
  [YEAR]: z.string().optional(),
  [DESCRIPTION]: z.string().optional(),
  [UPLOAD]: z.any().optional(),
  [RATING_TYPE]: z.string().optional(),
  [RATING_NAME]: z.string().optional(),
  [RATING_DESCRIPTION]: z.string().optional(),
  [THUMBNAIL_UPLOAD]: z.any().optional(),
  [UPLOAD_TRANSCRIPT]: z.any().optional(),
  [LINK]: z.any().optional(),
  [UPLOAD_LOGO]: z.any().optional(),
  [ADDITIONAL_UPLOAD]: z.array(z.object({
    file: z.any(),
    subTitle: z.string().min(1,"subtitle is required").optional()
  })).optional(),
};

// Conditional validation function
export const createDynamicSchema = (data: any) => {
  const { investorType, subType, type } = data;
  
  if (!investorType || !subType || !type) {
    return z.object(baseSchema);
  }

  const fieldConfig = getFieldConfig(investorType, subType, type);
  
  if (!fieldConfig) {
    return z.object(baseSchema);
  }

  const schemaFields: any = { ...baseSchema };

  // Add conditional validations based on field configuration
  // Update date validation to handle both string and Date objects
if (fieldConfig.fields.includes(DATE)) {
  schemaFields[DATE] = z.union([
    z.string().min(1, "Date is required"),
    z.date()
  ]).refine(
    (val) => val !== null && val !== undefined && val !== "",
    "Date is required"
  );
}
  

  if (fieldConfig.fields.includes(YEAR)) {
    schemaFields[YEAR] = z.string().min(1, "Year is required");
  }

  if (fieldConfig.fields.includes(DESCRIPTION)) {
    schemaFields[DESCRIPTION] = z.string().min(1, "Description is required");
  }

  if (fieldConfig.fields.includes(UPLOAD)) {
    schemaFields[UPLOAD] = z.any().optional();
  }

  if (fieldConfig.fields.includes(THUMBNAIL_UPLOAD)) {
    schemaFields[THUMBNAIL_UPLOAD] = z.any().refine((file) => file, "File is required");
  }

  if (fieldConfig.fields.includes(UPLOAD_TRANSCRIPT)) {
    schemaFields[UPLOAD_TRANSCRIPT] = z.any().refine((file) => file, "File is required");
  }

  if (fieldConfig.fields.includes(UPLOAD_LOGO)) {
    schemaFields[UPLOAD_LOGO] = z.any().refine((file) => file, "File is required");
  }

  if (fieldConfig.fields.includes(LINK)) {
    schemaFields[LINK] = z.string().optional();
  }

  if (fieldConfig.fields.includes(RATING_TYPE)) {
    schemaFields[RATING_TYPE] = z.string().min(1, "Rating type is required");
  }

  if (fieldConfig.fields.includes(RATING_NAME)) {
    schemaFields[RATING_NAME] = z.string().min(1, "Rating name is required");
  }

  if (fieldConfig.fields.includes(RATING_DESCRIPTION)) {
    schemaFields[RATING_DESCRIPTION] = z.string().min(1, "Rating description is required");
  }

  // --- XOR RULE FOR LINK vs UPLOAD ---
const isDisclosureLODR =
  investorType === "Governance & Policies" &&
  subType === "Disclosure under Regulation 46 and 62 of SEBI (LODR)" &&
  type === "Disclosure under Regulation 46 and 62 of SEBI (LODR)";

if (isDisclosureLODR) {
 return z.object(schemaFields).superRefine((values, ctx) => {
  const upload = values[UPLOAD];
  const link = values[LINK];

  const hasUpload = upload && (upload instanceof File || upload.attachmentId);
  const hasLink = link && String(link).trim() !== "";

  if (!hasUpload && !hasLink) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Either Upload or Link is required",
      path: [UPLOAD],
    });
  }

  if (hasUpload && hasLink) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Upload and Link cannot both be provided",
      path: [UPLOAD],
    });
  }
});

}


  return z.object(schemaFields);
};

// Default schema for initial validation
export const addSchema = z.object(baseSchema);

export type AddModelFormValues = {
  [INVESTOR_TYPE]: string;
  [SUB_TYPE]: string;
  [TYPE]: string;
  [TITLE]: string;
  [DATE]?: Date | string | null;
  [YEAR]?: string;
  [DESCRIPTION]?: string;
  [UPLOAD]?: any;
  [RATING_TYPE]?: string;
  [RATING_NAME]?: string;
  [RATING_DESCRIPTION]?: string;
  [THUMBNAIL_UPLOAD]?: any;
  [UPLOAD_TRANSCRIPT]?: any;
  [UPLOAD_LOGO]?: any;
  [LINK]?: string;
  [ADDITIONAL_UPLOAD]?: Array<{ file: any; subTitle?: string }>;
};